/**
 * ========== VideoPlayer.tsx ==========
 * 视频播放器组件 - 封装了 HTML5 video 元素和控制逻辑
 * 
 * 【类比 C++】
 * - 这是一个"可复用类"，被 Player.tsx 实例化使用
 * - Props 相当于构造函数参数
 * - forwardRef 允许父组件获取子组件的引用（类似 C++ 返回 this 指针）
 */

// ==================== 导入区 ====================
import { useState, useRef, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, Repeat1, Repeat, Bot, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import Hls from "hls.js";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

// ==================== 类型定义（相当于 C++ 的 struct/class 声明） ====================

/**
 * 组件的 Props 接口 - 定义父组件传入的参数
 * 类比 C++: 
 *   struct VideoPlayerProps {
 *     string src;
 *     double currentTime;
 *     std::function<void(double)> onTimeUpdate;
 *     std::function<void(double)> onDurationChange;
 *   };
 */
interface VideoPlayerProps {
  src: string;                              // 视频源 URL
  currentTime: number;                      // 当前播放时间（由父组件传入）
  onTimeUpdate: (time: number) => void;     // 时间更新回调（通知父组件）
  onDurationChange: (duration: number) => void;  // 时长变化回调
  sentenceLoopActive?: boolean;
  fullLoopActive?: boolean;
  onToggleSentenceLoop?: () => void;
  onToggleFullLoop?: () => void;
  onAnalyzeSentence?: () => void;
  hideInternalControls?: boolean;
  playbackRate?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  volume?: number;
  onVolumeChange?: (volume: number) => void;
  suspendVideo?: boolean;
  forceDetach?: boolean;
  carryoverTimeOnSourceSwitch?: boolean;
}

/**
 * 暴露给父组件的方法接口
 * 类比 C++: 
 *   class VideoPlayerHandle {
 *   public:
 *     virtual void seekTo(double time) = 0;
 *     virtual void play() = 0;
 *     virtual void pause() = 0;
 *   };
 */
export interface VideoPlayerHandle {
  seekTo: (time: number) => void;  // 跳转到指定时间
  play: () => void;                 // 播放
  pause: () => void;                // 暂停
}

// ==================== 主组件 ====================

/**
 * forwardRef 用法解释：
 * 
 * 普通组件：父组件无法直接调用子组件的方法
 * forwardRef：允许父组件通过 ref 获取子组件暴露的方法
 * 
 * 类比 C++:
 *   // 普通情况：子对象被封装，父类无法直接访问
 *   class Parent {
 *     private: Child child;
 *   };
 *   
 *   // forwardRef 相当于子类暴露接口：
 *   class Child {
 *   public:
 *     ChildHandle* getHandle() { return this; }
 *   };
 */
export const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>(
  ({ src, currentTime, onTimeUpdate, onDurationChange, sentenceLoopActive, fullLoopActive, onToggleSentenceLoop, onToggleFullLoop, onAnalyzeSentence, hideInternalControls, playbackRate: propPlaybackRate, onPlay, onPause, onEnded, volume: propVolume, onVolumeChange, suspendVideo = false, forceDetach = false, carryoverTimeOnSourceSwitch = true }, ref) => {
    
    // ========== useRef：DOM 引用和可变数据 ==========
    /**
     * useRef 有两个用途：
     * 1. 引用 DOM 元素（类似 document.getElementById）
     * 2. 存储可变数据但不触发重新渲染（类似 C++ 私有变量）
     * 
     * 区别于 useState：
     * - useState: 修改会触发组件重新渲染
     * - useRef: 修改不会触发重新渲染
     */
    const videoRef = useRef<HTMLVideoElement>(null);  // 引用 <video> DOM 元素
    const containerRef = useRef<HTMLDivElement>(null);  // 引用容器 <div>
    
    // ========== useState：需要触发 UI 更新的状态 ==========
    const initialVolume = Math.max(0, Math.min(1, Number(propVolume ?? 1)));
    const [isPlaying, setIsPlaying] = useState(false);     // 是否正在播放
    const [isMuted, setIsMuted] = useState(initialVolume === 0);         // 是否静音
    const [volume, setVolume] = useState(initialVolume);               // 音量 0-1
    const [duration, setDuration] = useState(0);           // 视频总时长
    const [playbackRate, setPlaybackRate] = useState(propPlaybackRate || 1);   // 播放速度
    const [showControls, setShowControls] = useState(true); // 是否显示控制栏
    const [borderless, setBorderless] = useState(true);   // 是否无边框模式
    const { toast } = useToast();
    const isMobile = useIsMobile();

    // ========== useRef：不需要触发 UI 更新的内部状态 ==========
    const pendingSeekRef = useRef<number | null>(null);    // 待处理的跳转时间
    const hideTimerRef = useRef<number | null>(null);      // 自动隐藏控制栏的定时器
    const seekingRef = useRef(false);                      // 是否正在跳转中（防止重复跳转）
    const lastNonZeroVolumeRef = useRef(initialVolume > 0 ? initialVolume : 1);
    const detachedAtRef = useRef<number | null>(null);
    const rafTimeSyncRef = useRef<number | null>(null);
    const rafLastEmitAtRef = useRef(0);
    const playbackTimeMirrorRef = useRef(currentTime);

    // ========== useCallback：缓存函数，避免不必要的重新创建 ==========
    /**
     * useCallback 用法：
     * const fn = useCallback(() => { ... }, [依赖项]);
     * 
     * 作用：只有当依赖项变化时才重新创建函数
     * 类比 C++: 类似于函数指针缓存，避免每次都创建新的 lambda
     */
    
    /**
     * 清除自动隐藏定时器
     */
    const clearHideTimer = useCallback(() => {
      if (hideTimerRef.current) {
        window.clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }
    }, []);  // 空依赖数组 = 只在组件首次渲染时创建一次

    /**
     * 设置自动隐藏控制栏的定时器
     * @param ms - 延迟毫秒数
     */
    const scheduleAutoHide = useCallback(
      (ms = 2000) => {
        clearHideTimer();
        // 只在播放时自动隐藏
        if (!isPlaying) return;
        hideTimerRef.current = window.setTimeout(() => {
          setShowControls(false);
        }, ms);
      },
      [clearHideTimer, isPlaying]  // 依赖这两个变量
    );

    /**
     * 执行视频跳转 - 核心跳转逻辑
     * @param time - 目标时间（秒）
     * 
     * 这是最复杂的函数之一，处理了多种边界情况：
     * 1. 视频元数据未加载完成时的处理
     * 2. 防止重复跳转导致的卡顿
     * 3. 时间范围限制
     */
    const performSeek = useCallback(
      (time: number) => {
        const video = videoRef.current;
        if (!video) return;

        // 防止重入：如果正在跳转中，忽略新的跳转请求
        // 类比 C++: if (mutex.try_lock() == false) return;
        if (seekingRef.current) return;

        // 记录待跳转时间（用于元数据加载后重试）
        pendingSeekRef.current = time;

        // 检查视频是否已加载元数据
        // readyState: 0=无数据, 1=元数据, 2=当前帧, 3=未来帧, 4=足够数据
        if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration === 0) {
          return;  // 元数据未准备好，等待 loadedmetadata 事件
        }

        // 限制时间范围在 [0, duration] 之间
        // 类比 C++: time = std::clamp(time, 0.0, video.duration);
        const clampedTime = Math.max(0, Math.min(time, video.duration));
        
        // 如果已经在目标时间附近（0.1秒内），跳过跳转
        if (Math.abs(video.currentTime - clampedTime) < 0.1) {
          pendingSeekRef.current = null;
          return;
        }

        // 标记跳转开始
        seekingRef.current = true;

        // 设置视频当前时间（触发跳转）
        try {
          video.currentTime = clampedTime;
        } catch {
          // 某些情况下可能抛出异常，忽略
        }

        // 300ms 后解锁，允许下次跳转
        // 类比 C++: std::thread([&]{ sleep(300); mutex.unlock(); }).detach();
        window.setTimeout(() => {
          pendingSeekRef.current = null;
          seekingRef.current = false;
        }, 300);
      },
      []
    );

    // ========== useImperativeHandle：暴露方法给父组件 ==========
    /**
     * 这个 Hook 定义了父组件通过 ref 能调用的方法
     * 
     * 类比 C++:
     *   class VideoPlayer : public VideoPlayerHandle {
     *   public:
     *     void seekTo(double time) override { performSeek(time); }
     *     void play() override { videoElement->play(); }
     *     void pause() override { videoElement->pause(); }
     *   };
     */
    useImperativeHandle(ref, () => ({
      seekTo: (time: number) => {
        performSeek(time);
      },
      play: () => {
        const p = videoRef.current?.play();
        if (p && typeof p.catch === "function") p.catch(() => undefined);
      },
      pause: () => videoRef.current?.pause(),
    }));

    const applyVolume = useCallback(
      (nextVolume: number, emitChange = true) => {
        const clamped = Math.max(0, Math.min(1, Number(nextVolume || 0)));
        if (videoRef.current) {
          videoRef.current.volume = clamped;
          videoRef.current.muted = false;
        }
        setVolume(clamped);
        setIsMuted(clamped === 0);
        if (clamped > 0) {
          lastNonZeroVolumeRef.current = clamped;
        }
        if (emitChange) {
          onVolumeChange?.(clamped);
        }
      },
      [onVolumeChange],
    );

    // 监听 props 中的 playbackRate 变化并应用
    useEffect(() => {
      if (propPlaybackRate !== undefined && videoRef.current) {
        videoRef.current.playbackRate = propPlaybackRate;
        setPlaybackRate(propPlaybackRate);
      }
    }, [propPlaybackRate]);

    // 监听外部音量（受控模式）
    useEffect(() => {
      if (propVolume === undefined) return;
      applyVolume(propVolume, false);
    }, [propVolume, applyVolume]);

    useEffect(() => {
      if (Number.isFinite(currentTime)) {
        playbackTimeMirrorRef.current = currentTime;
      }
    }, [currentTime]);

    // ========== useEffect：HLS/Video Source Loading ==========
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      let hls: Hls | null = null;
      const normalizedSrc = typeof src === "string" ? src.trim() : "";

      const isHlsSource = (url: string) => {
        const normalized = String(url || "").trim().toLowerCase();
        if (!normalized) return false;
        if (normalized.includes(".m3u8") || normalized.includes(".m3u")) return true;
        try {
          const parsed = new URL(normalized, window.location.origin);
          const path = parsed.pathname.toLowerCase();
          if (path.endsWith(".m3u8") || path.endsWith(".m3u")) return true;
          for (const value of parsed.searchParams.values()) {
            const lower = String(value || "").toLowerCase();
            if (lower.includes(".m3u8") || lower.includes(".m3u")) return true;
          }
        } catch {
          // ignore URL parse errors and fallback to false
        }
        return false;
      };

      const stopAndClearSource = () => {
        const doc = document as Document & {
          pictureInPictureElement?: Element | null;
          exitPictureInPicture?: () => Promise<void>;
        };
        try {
          if (doc.pictureInPictureElement === video && doc.exitPictureInPicture) {
            void doc.exitPictureInPicture().catch(() => undefined);
          }
        } catch {
          // ignore
        }
        try {
          video.pause();
        } catch {
          // ignore
        }
        try {
          video.removeAttribute("src");
          video.load();
        } catch {
          // ignore
        }
      };

      if (forceDetach) {
        const mediaTime = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const propTime = Number.isFinite(playbackTimeMirrorRef.current) ? playbackTimeMirrorRef.current : 0;
        detachedAtRef.current = Math.max(mediaTime, propTime, 0);
        stopAndClearSource();
        return;
      }

      // Stop the previous stream first, otherwise some Android browsers may keep it
      // alive as a floating layer when switching episodes.
      if (carryoverTimeOnSourceSwitch) {
        if (Number.isFinite(video.currentTime) && video.currentTime > 0.05) {
          const propTime = Number.isFinite(playbackTimeMirrorRef.current) ? playbackTimeMirrorRef.current : 0;
          detachedAtRef.current = Math.max(video.currentTime, propTime, detachedAtRef.current ?? 0);
        }
      } else {
        // Live/demo switching should always start from 0 and never inherit previous episode time.
        detachedAtRef.current = null;
        pendingSeekRef.current = null;
      }
      stopAndClearSource();

      if (carryoverTimeOnSourceSwitch && detachedAtRef.current != null) {
        pendingSeekRef.current = detachedAtRef.current;
        detachedAtRef.current = null;
      }

      if (!normalizedSrc) {
        return;
      }

      if (isHlsSource(normalizedSrc)) {
        const canUseNativeHls = video.canPlayType("application/vnd.apple.mpegurl") !== "";
        if (canUseNativeHls) {
          // Prefer native HLS on Apple devices for better compatibility.
          if (video.src !== normalizedSrc) {
            video.src = normalizedSrc;
          }
        } else if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(normalizedSrc);
          hls.attachMedia(video);
          
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // Manifest loaded
          });
          
          hls.on(Hls.Events.ERROR, (event, data) => {
             if (data.fatal) {
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    hls?.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    hls?.recoverMediaError();
                    break;
                  default:
                    // cannot recover
                    hls?.destroy();
                    break;
                }
             }
          });
        } else if (video.src !== normalizedSrc) {
          // Browser cannot use hls.js; fallback to native src assignment.
          video.src = normalizedSrc;
        }
      } else {
        // Standard MP4 or other formats
        // Only set src if it's different to avoid reloading
        if (video.src !== normalizedSrc) {
          video.src = normalizedSrc;
        }
      }

      return () => {
        if (hls) {
          hls.destroy();
        }
        stopAndClearSource();
      };
    }, [src, forceDetach, carryoverTimeOnSourceSwitch]);

    // ========== useEffect：副作用处理（事件监听、API 调用等） ==========
    /**
     * useEffect 用法：
     * useEffect(() => {
     *   // 组件挂载或依赖项变化时执行
     *   return () => {
     *     // 组件卸载或依赖项变化前执行（清理）
     *   };
     * }, [依赖项]);
     * 
     * 类比 C++:
     *   Constructor() { ... }  // 挂载
     *   Destructor() { ... }   // 卸载
     */
    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      // ===== 事件处理函数定义 =====
      
      // 播放时间更新（每秒触发多次）
      const handleTimeUpdate = () => onTimeUpdate(video.currentTime);
      
      // 视频时长变化
      const handleDurationChange = () => {
        setDuration(video.duration);
        onDurationChange(video.duration);
      };
      
      // 跳转事件
      const handleSeeking = () => onTimeUpdate(video.currentTime);
      
      // 播放/暂停事件
      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };
      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };
      const handleEnded = () => {
        setIsPlaying(false);
        onPause?.();
        onEnded?.();
      };

      // 元数据加载完成 - 处理待处理的跳转
      const handleReady = () => {
        if (pendingSeekRef.current != null) {
          const t = pendingSeekRef.current;
          performSeek(t);
        }
      };

      // 错误处理
      const handleError = (e: any) => {
        const error = video.error;
        console.error("Video Error:", error, e);
        let msg = "视频加载失败";
        if (error) {
            switch (error.code) {
                case 1: msg = "视频播放被终止"; break;
                case 2: msg = "网络错误，视频下载失败"; break;
                case 3: msg = "视频解码失败，格式可能不支持"; break;
                case 4: msg = "视频资源不存在或无法访问"; break;
            }
        }
        toast({
            title: "播放错误",
            description: msg,
            variant: "destructive",
        });
      };

      // ===== 添加事件监听器 =====
      // 类比 C++: video->addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("timeupdate", handleTimeUpdate);
      video.addEventListener("durationchange", handleDurationChange);
      video.addEventListener("seeking", handleSeeking);
      video.addEventListener("seeked", handleSeeking);
      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("ended", handleEnded);
      video.addEventListener("loadedmetadata", handleReady);
      video.addEventListener("canplay", handleReady);
      video.addEventListener("error", handleError);

      // ===== 清理函数：组件卸载时移除事件监听器 =====
      // 类比 C++: Destructor 中的 video->removeEventListener(...)
      return () => {
        video.removeEventListener("timeupdate", handleTimeUpdate);
        video.removeEventListener("durationchange", handleDurationChange);
        video.removeEventListener("seeking", handleSeeking);
        video.removeEventListener("seeked", handleSeeking);
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("ended", handleEnded);
        video.removeEventListener("loadedmetadata", handleReady);
        video.removeEventListener("canplay", handleReady);
        video.removeEventListener("error", handleError);
      };
    }, [onEnded, onPause, onTimeUpdate, onDurationChange, performSeek, toast]);

    // Mobile browsers (especially iOS Safari) dispatch `timeupdate` sparsely.
    // Add a lightweight RAF sync so subtitle highlighting follows audio/video more tightly.
    useEffect(() => {
      if (!isMobile || !isPlaying) {
        if (rafTimeSyncRef.current != null) {
          cancelAnimationFrame(rafTimeSyncRef.current);
          rafTimeSyncRef.current = null;
        }
        return;
      }

      const emitIntervalMs = 90;
      const tick = (ts: number) => {
        const video = videoRef.current;
        if (!video || video.paused || video.ended) {
          rafTimeSyncRef.current = null;
          return;
        }

        if (ts - rafLastEmitAtRef.current >= emitIntervalMs) {
          onTimeUpdate(video.currentTime);
          rafLastEmitAtRef.current = ts;
        }

        rafTimeSyncRef.current = requestAnimationFrame(tick);
      };

      rafLastEmitAtRef.current = performance.now();
      rafTimeSyncRef.current = requestAnimationFrame(tick);

      return () => {
        if (rafTimeSyncRef.current != null) {
          cancelAnimationFrame(rafTimeSyncRef.current);
          rafTimeSyncRef.current = null;
        }
      };
    }, [isMobile, isPlaying, onTimeUpdate]);

    // 自动隐藏控制栏
    useEffect(() => {
      if (isPlaying && showControls) scheduleAutoHide(2200);
      return () => clearHideTimer();
    }, [isPlaying, showControls, scheduleAutoHide, clearHideTimer]);

    // Some Android browsers keep video as a hardware overlay layer.
    // Pause while settings panel is open to avoid overlay bleeding through dialogs.
    useEffect(() => {
      if (!suspendVideo) return;
      const video = videoRef.current;
      if (!video) return;
      try {
        video.pause();
      } catch {
        // ignore
      }
    }, [suspendVideo]);

    // ========== 用户交互处理函数 ==========
    
    /**
     * 切换播放/暂停
     */
    const togglePlay = () => {
      if (videoRef.current) {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          // play() 返回 Promise，需要处理可能的错误
          const p = videoRef.current.play();
          if (p && typeof p.catch === "function") p.catch(() => undefined);
        }
        setShowControls(true);
        scheduleAutoHide(2200);
      }
    };

    /**
     * 切换静音
     */
    const toggleMute = () => {
      if (isMuted || volume === 0) {
        applyVolume(lastNonZeroVolumeRef.current || 1);
      } else {
        applyVolume(0);
      }
    };

    /**
     * 音量变化处理
     * @param value - Slider 组件返回的值数组
     */
    const handleVolumeChange = (value: number[]) => {
      const vol = value[0];
      applyVolume(vol);
    };

    /**
     * 进度条拖动处理
     * @param value - Slider 组件返回的值数组
     */
    const handleSeek = (value: number[]) => {
      if (videoRef.current) {
        videoRef.current.currentTime = value[0];
        onTimeUpdate(value[0]);
      }
    };

    /**
     * 切换播放速度
     * 循环切换：0.5 -> 0.75 -> 1 -> 1.25 -> 1.5 -> 2 -> 0.5
     */
    const handleRateChange = () => {
      const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
      const currentIndex = rates.indexOf(playbackRate);
      const nextRate = rates[(currentIndex + 1) % rates.length];
      if (videoRef.current) {
        videoRef.current.playbackRate = nextRate;
        setPlaybackRate(nextRate);
      }
    };

    /**
     * 切换全屏
     */
    const handleFullscreen = () => {
      if (containerRef.current) {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          containerRef.current.requestFullscreen();
        }
      }
    };

    /**
     * 后退 5 秒
     */
    const rewind5 = () => {
      if (videoRef.current) {
        videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5);
      }
    };

    /**
     * 格式化时间显示
     * @param seconds - 秒数
     * @returns 格式化字符串，如 "3:05"
     */
    const formatTime = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    /**
     * 视频区域点击处理
     * 点击视频只切换边框模式，不控制播放（播放由按钮控制）
     * 手机端例外：点击视频切换控制栏显示
     */
    const handleVideoClick = (e: React.MouseEvent<HTMLVideoElement>) => {
      e.stopPropagation();
      togglePlay();
    };

    // 播放按钮可见性：非无边框模式时显示
    const showFixedPlayButton = !borderless;

    // ========== JSX 渲染 ==========
    return (
      <div
        ref={containerRef}
        className={cn("relative isolate overflow-hidden group transition-all duration-300 rounded-xl bg-black flex flex-col w-full h-full")}
        onPointerDown={() => {
          setShowControls(true);
          scheduleAutoHide(2200);
        }}
      >
        {/* ===== 视频元素 ===== */}
        {suspendVideo && <div className="w-full flex-1 min-h-0 bg-black" />}
        <video
          ref={videoRef}
          // src={src}  <-- REMOVE THIS to let useEffect handle it
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload noremoteplayback"
          preload="metadata"
          className={cn(
            "relative z-0 w-full flex-1 min-h-0 object-contain object-center bg-black",
            suspendVideo && "hidden",
          )}
          onClick={handleVideoClick}
        />

        {/* 中心播放按钮（暂停时显示） */}
        {!isPlaying && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              togglePlay();
            }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 h-16 w-16 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm shadow-xl"
          >
            <Play className="h-7 w-7 ml-0.5" />
          </Button>
        )}

        

        {/* ===== 底部控制栏（可隐藏以便外部分离 UI） ===== */}
        {!hideInternalControls && (
          <div className="z-20 w-full bg-white/95 backdrop-blur border-t border-border dark:bg-neutral-900/90">
            <div className="px-2.5 py-1.5 sm:px-4 sm:py-2">
            {/* 进度条 */}
            <div className="mb-1.5 sm:mb-2">
              <Slider
                value={[currentTime]}
                max={duration || 100}
                step={0.1}
                onValueChange={handleSeek}
                className="cursor-pointer"
              />
            </div>

            {/* 控制按钮行 */}
            <div className="flex items-center justify-between gap-2 max-[390px]:gap-1.5">
              {/* 左侧按钮组 */}
              <div className="flex min-w-0 items-center gap-1.5 sm:gap-2">
                {/* 播放/暂停 */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePlay}
                  className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                >
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>

                {/* 后退 5 秒 */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={rewind5}
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90 max-[390px]:hidden"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>

                {/* 音量控制 */}
                <div className="flex items-center gap-1 sm:gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleMute}
                    className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                  >
                    {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
                  </Button>
                  <div className="w-16 sm:w-24 max-[390px]:hidden">
                    <Slider
                      value={[volume]}
                      max={1}
                      step={0.05}
                      onValueChange={handleVolumeChange}
                    />
                  </div>
                </div>

                {/* 时间显示 */}
                <span className="text-muted-foreground text-xs sm:text-sm ml-1.5 sm:ml-2 whitespace-nowrap tabular-nums max-[390px]:text-[11px]">
                  {formatTime(currentTime)}
                  <span className="max-[390px]:hidden"> / {formatTime(duration)}</span>
                </span>
              </div>

              {/* 右侧按钮组 */}
              <div className="flex items-center shrink-0 gap-1 sm:gap-2">
                {/* 播放速度 */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRateChange}
                  className="rounded-full bg-white border border-border px-2 sm:px-3 text-xs font-mono shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90 h-8 sm:h-10"
                >
                  {playbackRate}x
                </Button>

                {/* AI 整句解析 */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onAnalyzeSentence}
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90 max-[390px]:hidden"
                  title="AI 整句解析"
                >
                  <Bot className="h-4 w-4" />
                </Button>

                {/* 全屏 */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleFullscreen}
                  className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white border border-border shadow-sm hover:bg-white/90 dark:bg-neutral-700 dark:hover:bg-neutral-700/90"
                >
                  <Maximize className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          </div>
        )}

        {/* 注意：点击视频只切换边框模式，播放控制仅通过按钮 */}
      </div>
    );
  }
);

// 设置组件显示名称（用于 React DevTools 调试）
VideoPlayer.displayName = "VideoPlayer";
